import { api } from '../../../api/client';
import type {
  FieldIntakeDetail, FieldIntakeLookups, FieldIntakeRow, JobCreated, Permissions, ReceiptInput,
} from './types';

const BASE = '/api/v1/field-intake';

/** Services with a submitted field record; `from` (yyyy-mm-dd) limits by collection date. */
export async function fetchFieldIntakeList(from: string | null): Promise<FieldIntakeRow[]> {
  const res = await api.get<{ data: FieldIntakeRow[] }>(BASE, { params: { from: from || undefined } });
  return res.data.data ?? [];
}

export async function fetchFieldIntakePermissions(): Promise<Permissions> {
  const res = await api.get<{ data: Permissions }>(`${BASE}/permissions`, { silentLoading: true } as object);
  return res.data.data;
}

export async function fetchFieldIntakeLookups(): Promise<FieldIntakeLookups> {
  const res = await api.get<{ data: FieldIntakeLookups }>(`${BASE}/lookups`);
  return res.data.data;
}

export async function fetchFieldIntakeDetail(serviceTaskId: string): Promise<FieldIntakeDetail> {
  const res = await api.get<{ data: FieldIntakeDetail }>(`${BASE}/${serviceTaskId}`);
  return res.data.data;
}

export async function saveFieldIntakeReceipt(serviceTaskId: string, input: ReceiptInput): Promise<number> {
  const res = await api.put<{ data: { version: number } }>(`${BASE}/${serviceTaskId}/receipt`, input);
  return res.data.data.version;
}

/** Creates the Job; `reviewStamp` is the detail's header.reviewStamp — the server refuses
 *  (RECORD_CHANGED) when the service changed since this page loaded it. */
export async function createFieldIntakeJob(serviceTaskId: string, reviewStamp: string | null): Promise<JobCreated> {
  const res = await api.post<{ data: JobCreated }>(`${BASE}/${serviceTaskId}/job`, { reviewStamp });
  return res.data.data;
}

/** A photograph's image as an object URL (the caller revokes it). */
export async function fetchFieldIntakePhotoUrl(serviceTaskId: string, photoId: string): Promise<string> {
  const res = await api.get(`${BASE}/${serviceTaskId}/photos/${encodeURIComponent(photoId)}/content`,
    { responseType: 'blob', silentLoading: true } as object);
  return URL.createObjectURL(res.data as Blob);
}

/** Downloads the eCOC workbook (.xlsx). */
export async function downloadFieldIntakeWorkbook(serviceTaskId: string, serviceNo: string): Promise<void> {
  const res = await api.get(`${BASE}/${serviceTaskId}/ecoc/workbook`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${serviceNo}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Opens the Field Survey Report PDF in a new tab; false when the browser blocked it. */
export async function openFieldIntakeReport(serviceTaskId: string): Promise<boolean> {
  const res = await api.get(`${BASE}/${serviceTaskId}/report`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const w = window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return w !== null;
}
